import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {RoutingModule} from '../../../routing.module';

// @Injectable({ providedIn: RoutingModule })
@Injectable({ providedIn: 'root' })
export class RuleRoutingActionsService {
    selectedAction: any;
    currentTitle: string;

    clearSelection(): void {
        this.selectedAction = null;
        this.currentTitle = null;
    }

    removeAction(actionsArray, index = null): void {
        const startIndex = !_.isNil(index) ? index : actionsArray.findIndex(this.findFromSelectedById);

        if (startIndex > -1) {
            actionsArray.splice(startIndex, 1);
            if (actionsArray.length === 0) {
                this.selectedAction = null;
            } else {
                this.selectedAction = actionsArray[startIndex === actionsArray.length ? startIndex - 1 : startIndex];
            }
        }
    }

    addAction(actionsArray): void {
        const newAction = {
            dataItem: {
                title: 'New Action',
                id: this.createItemId(actionsArray),
                isEquallyBalanced: true,
                isOpen: true,
                actions: [{
                    continueWithNodeInternalTables: false,
                    discard: false,
                    registeredUsers: false,
                    routingAttempts: 1,
                    percentage: 100,
                    isEquallyBalanced: true,
                    isNormalizationCollapsed: true,
                    srcNormalizationFromHeader: true,
                    srcNormalizationPAIHeader: true,
                    srcNormalizationPPIHeader: true
                }]
            }
        };

        this.updateActionsArray(actionsArray, newAction);
        this.selectedAction = newAction.dataItem;
    }

    addLoopbackAction(actionsArray): void {
        const newAction = {
            dataItem: {
                title: 'Loopback Action',
                id: this.createItemId(actionsArray),
                isEquallyBalanced: true,
                isOpen: true,
                actions: [{
                    continueWithNodeInternalTables: false,
                    discard: false,
                    registeredUsers: false,
                    loopback: true,
                    routingAttempts: 1,
                    percentage: 100,
                    isEquallyBalanced: true,
                    isNormalizationCollapsed: true,
                    srcNormalizationFromHeader: true,
                    srcNormalizationPAIHeader: true,
                    srcNormalizationPPIHeader: true
                }]
            }
        };

        this.updateActionsArray(actionsArray, newAction);
        this.selectedAction = newAction.dataItem;
    }

    updateActionsArray(actionsArray, newAction): void {
        this.closeAllOpenItems(actionsArray);

        if (this.hasSelection() && !this.isDiscardOrContinueAction(this.selectedAction.actions[0])) {
            actionsArray.splice(actionsArray.findIndex(this.findFromSelectedById) + 1, 0, newAction.dataItem);
        } else if ((this.hasSelection() && this.isDiscardOrContinueAction(this.selectedAction.actions[0]))
          || this.atLeastOneDiscardOrContinueActionExist(actionsArray)) {
            actionsArray.splice(actionsArray.length - 1, 0, newAction.dataItem);
        } else {
            actionsArray.push(newAction.dataItem);
        }
    }

    addDiscardAction(actionsArray): void {
        const newAction = {
            dataItem: {
                title: 'Discard Action',
                id: this.createItemId(actionsArray),
                isEquallyBalanced: false,
                isOpen: true,
                actions: [{
                    continueWithNodeInternalTables: false,
                    discard: true,
                    registeredUsers: false,
                    sipReason: 1,
                    isSipReasonDefined: false,
                    percentage: 100,
                    isEquallyBalanced: false
                }]
            }
        };

        this.closeAllOpenItems(actionsArray);
        actionsArray.splice(actionsArray.length, 0, newAction.dataItem);
        this.selectedAction = newAction.dataItem;
    }

    addContinueAction(actionsArray): void {
        const newAction = {
            dataItem: {
                title: 'Continue with Node`s internal routing table',
                id: this.createItemId(actionsArray),
                isEquallyBalanced: false,
                isOpen: true,
                actions: [{
                    continueWithNodeInternalTables: true,
                    discard: false,
                    registeredUsers: false,
                    sipReason: null,
                    isSipReasonDefined: false,
                    percentage: 100,
                    isEquallyBalanced: false
                }]
            }
        };

        this.closeAllOpenItems(actionsArray);
        actionsArray.splice(actionsArray.length, 0, newAction.dataItem);
        this.selectedAction = newAction.dataItem;
    }

    addRegisteredUserAction(actionsArray): void {
        const newAction = {
            dataItem: {
                title: 'Registered users action',
                id: this.createItemId(actionsArray),
                isEquallyBalanced: false,
                isOpen: true,
                actions: [{
                    continueWithNodeInternalTables: false,
                    discard: false,
                    registeredUsers: true,
                    sipReason: null,
                    isSipReasonDefined: false,
                    percentage: 100,
                    isEquallyBalanced: false
                }]
            }
        };

        this.updateActionsArray(actionsArray, newAction);
        this.selectedAction = newAction.dataItem;
    }

    moveUp(actionsArray, currentIndex = null): void {
        const index = currentIndex ? currentIndex : actionsArray.findIndex(this.findFromSelectedById);

        if (index > -1 && index - 1 >= 0) {
            this.swapActions(index, index - 1, actionsArray);
            this.selectedAction = actionsArray[index - 1];
        }
    }

    moveDown(actionsArray, currentIndex = null): void {
        const index = currentIndex ? currentIndex : actionsArray.findIndex(this.findFromSelectedById);

        if (index > -1 && index + 1 <= actionsArray.length - 1) {
            this.swapActions(index, index + 1, actionsArray);
            this.selectedAction = actionsArray[index + 1];
        }
    }

    addRuleAction(context): void {
        context.actions.push({
            discard : false,
            continueWithNodeInternalTables: false,
            registeredUsers: false,
            isNormalizationCollapsed: true,
            routingAttempts: context.actions.length > 0 ? context.actions[0].routingAttempts : 1,
            srcNormalizationFromHeader: true,
            srcNormalizationPAIHeader: true,
            srcNormalizationPPIHeader: true
        });

        this.updatePercentage(context.actions, context.isEquallyBalanced);
    }

    removeRuleAction(context, action): void {
        const index = context.actions.findIndex((item) => {
            return item === action;
        });

        context.actions.splice(index, 1);

        this.updatePercentage(context.actions, context.isEquallyBalanced);
        this.updateRoutingAttempts(context.actions);
    }

    updateLoadBalancing(context, isEquallyBalanced): void {
        this.updatePercentage(context.actions, isEquallyBalanced);
    }

    setSelectedAction(selection): void {
        this.selectedAction = selection;
    }

    updateActionTitle(value, actions): void {
        const title = this.updateMainActionTitle(value);
        this.currentTitle = title;
        actions[actions.findIndex(this.findFromSelectedById)].title = value === null ? title : `[${value.elementType}] ${title}`;
    }

    hasSelection(): boolean {
        return !_.isUndefined(this.selectedAction) && this.selectedAction !== null;
    }

    isDiscardOrContinueActionSelected(actions, index): boolean {
        if (!_.isUndefined(actions[index]) && actions[index] !== null) {
            if (!_.isUndefined(actions[index].actions) &&  !_.isUndefined(actions[index].actions[0])) {
                return this.isDiscardOrContinueAction(actions[index].actions[0]);
            }
        }
        return false;
    }

    isFirst(actions, index = null): boolean {
        return index === 0;
    }

    isLast(actions, currentIndex = null): boolean {
        if (_.isNumber(currentIndex)) {
            return currentIndex === actions.length - 1 ||
              actions[currentIndex + 1].actions[0].discard ||
              actions[currentIndex + 1].actions[0].continueWithNodeInternalTables;
        }
        return true;
    }

    atLeastOneDiscardOrContinueActionExist(context): boolean {
        return this.atLeastOneDiscardActionExist(context) ||
            this.atLeastOneContinueActionExist(context);
    }

    atLeastOneLoopBackActionActionExist(context): boolean {
        return context.some((action) => {
            return action.actions[0].loopback;
        });
    }

    private isDiscardOrContinueAction(action): boolean {
        return action.discard || action.continueWithNodeInternalTables;
    }

    private atLeastOneDiscardActionExist(context): boolean {
        return context.some((action) => {
            return action.actions[0].discard;
        });
    }

    private atLeastOneContinueActionExist(context): boolean {
        return context.some((action) => {
            return action.actions[0].continueWithNodeInternalTables;
        });
    }

    isRegisteredUserActionDefined(context): any {
        return context.filter((action) => {
            return action.actions[0].registeredUsers;
        }).length > 0;
    }

    isSelected(action): boolean {
        if (this.selectedAction) {
            return action.id === this.selectedAction.id;
        }
        return false;
    }

    onToggle(action): void {
        if (!_.isUndefined(action.isOpen)) {
            action.isOpen = !action.isOpen;
        } else {
            action.isOpen = true;
        }
    }

    private updateRoutingAttempts(actions): any {

        if (actions.length === 0) {
            return;
        }

        if (actions[0].routingAttempts > actions.length) {
            actions[0].routingAttempts--;

            actions.forEach((action) => {
                action.routingAttempts = actions[0].routingAttempts;
            });
        }
    }

    private updatePercentage(actions, isEquallyBalanced): any {
        let j;
        let integerDivision;

        if (actions.length === 0) {
            return;
        }

        if (actions.length === 1) {
            actions[0].percentage = 100;
            actions[0].isEquallyBalanced = isEquallyBalanced;
            return;
        }

        if (isEquallyBalanced) {
            for (j = 0; j < actions.length; j++) {
                actions[j].isEquallyBalanced  = true;
                actions[j].percentage = 100;
            }
        } else {
            integerDivision = Math.floor(100 / (actions.length));

            for (j = 0; j < actions.length - 1; j++) {
                actions[j].isEquallyBalanced  = false;
                actions[j].percentage = integerDivision;
            }

            actions[actions.length - 1].percentage = 100 - integerDivision * (actions.length - 1);
            actions[actions.length - 1].isEquallyBalanced  = false;
        }
    }

    private createItemId(dataArray): number {
        const dataArrayCount = dataArray.length;
        let newId = 0;

        for (let i = 0; i < dataArrayCount; i++) {
            if (newId < dataArray[i].id) {
                newId = dataArray[i].id;
            }
        }
        return (newId + 1);

    }

    private updateMainActionTitle(value): string {
        return value === null || value.id === -1 ? 'New Action' : value.name;
    }

    private findFromSelectedById = (item) => {
        return this.selectedAction.id === item.id;
    }

    private swapActions(indexA, indexB, actionArray: void): void {
        const item = actionArray[indexA];
        actionArray[indexA] = actionArray[indexB];
        actionArray[indexB] = item;
    }

    private closeAllOpenItems(actionsArray): any {
        actionsArray.forEach(item => {
            if (item.isOpen) { item.isOpen = false; }
        });
    }
}
